/**
 * Goalkeeper - AI-controlled goalkeeper that guards the goal line.
 * Tracks ball X position, strafes to stay between ball and goal center,
 * and dives when the ball is very close.
 * Can pick up the ball and throw it towards teammates.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { createGoalkeeperMesh, HumanoidAnimator, KickType } from "./HumanoidMesh";

const GK_SPEED = 6;
const GK_DIVE_SPEED = 12;
const GK_DIVE_THRESHOLD_TIME = 0.8;
const GK_REACTION_TIME = 0.12;
const GOAL_HALF_WIDTH = 5;
const GOAL_HEIGHT = 3.5;

// Goalkeeper ball pickup and throw settings - REDUCED for shorter, more accurate throws
const PICKUP_RANGE = 1.5;
const THROW_POWER = 8;           // Reduced from 15 for shorter throws
const THROW_HOLD_TIME = 1.0;
const THROW_ARC = 0.25;          // Reduced from 0.4 for flatter trajectory
const MAX_THROW_DISTANCE = 12;   // Maximum distance to throw

interface Prediction {
  x: number;
  y: number;
  time: number;
  onTarget: boolean;
}

export class Goalkeeper {
  public mesh: BABYLON.Mesh;
  public animator: HumanoidAnimator;
  public isGoalCelebration = false;  // Flag to disable ball interaction during celebration
  private _ball: Ball;
  private _goalZ: number;
  private _collider: BABYLON.Mesh;
  private _currentSpeed = 0;
  private _velocity = new BABYLON.Vector3();
  private _diving = false;
  private _diveTimer = 0;
  private _diveDirection = 0;
  private _diveIsHigh = false;
  private _prediction: Prediction | null = null;
  private _reactionTimer = 0;
  private _lastBallVel = new BABYLON.Vector3();
  
  private _hasBall = false;
  private _throwTimer = 0;
  private _isOpponentGK = true;
  private _isHoldingBall = false;
  private _ballHoldTimer = 0;
  
  private _getTeammates: (() => BABYLON.Vector3[]) | null = null;
  private _ballAttachmentPoint: BABYLON.TransformNode | null = null;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    goalZ: number,
    jerseyColor: BABYLON.Color3,
    shadowGenerator?: BABYLON.ShadowGenerator,
    isOpponentGK: boolean = true
  ) {
    this._ball = ball;
    this._goalZ = goalZ;
    this._isOpponentGK = isOpponentGK;

    const gk = createGoalkeeperMesh("goalkeeper", scene, jerseyColor, shadowGenerator);
    this.mesh = gk.root;
    this.mesh.position = new BABYLON.Vector3(0, 0, goalZ);
    this.animator = new HumanoidAnimator(gk.limbs);

    this._collider = BABYLON.MeshBuilder.CreateCylinder(
      "gkCollider",
      { height: 1.8, diameter: 0.8, tessellation: 10 },
      scene
    );
    this._collider.isVisible = false;
    this._collider.physicsImpostor = new BABYLON.PhysicsImpostor(
      this._collider,
      BABYLON.PhysicsImpostor.CylinderImpostor,
      { mass: 0, restitution: 0.1, friction: 0.2 },
      scene
    );
  }
  
  setTeammateProvider(callback: () => BABYLON.Vector3[]): void {
    this._getTeammates = callback;
  }
  
  get hasBall(): boolean {
    return this._hasBall;
  }

  private _syncCollider(): void {
    if (this._diving) {
      this._collider.rotation.z = Math.PI / 2;
      this._collider.position.set(
        this.mesh.position.x,
        this._diveIsHigh ? 1.8 : 0.5, 
        this.mesh.position.z
      );
    } else {
      this._collider.rotation.z = 0;
      this._collider.position.set(
        this.mesh.position.x,
        0.9,
        this.mesh.position.z
      );
    }
  }

  private _predictBall(): Prediction | null {
    const ballPos = this._ball.mesh.position;
    const ballImp = this._ball.mesh.physicsImpostor;
    if (!ballImp) return null;

    const vel = ballImp.getLinearVelocity();
    if (!vel || Math.abs(vel.z) < 0.1) return null;

    if (Math.sign(vel.z) !== Math.sign(this._goalZ)) return null;

    const dz = this._goalZ - ballPos.z;
    const time = dz / vel.z;

    if (time <= 0 || time > 3.0) return null;

    const x = ballPos.x + vel.x * time;
    const y = ballPos.y + vel.y * time - 0.5 * 9.81 * time * time;
    const onTarget = Math.abs(x) < GOAL_HALF_WIDTH + 0.5 && y >= 0 && y < GOAL_HEIGHT + 0.5;

    return { x, y, time, onTarget };
  }

  update(dt: number): void {
    // During goal celebration, just animate and return
    if (this.isGoalCelebration) {
      this._currentSpeed = 0;
      this.animator.animate(dt, 0, false);
      this._syncCollider();
      return;
    }
    
    const ballPos = this._ball.mesh.position;
    const myPos = this.mesh.position;

    if (this._isHoldingBall) {
      this._updateBallHolding(dt);
      return;
    }

    if (!this._hasBall && !this._diving) {
      const distToBall = BABYLON.Vector3.Distance(myPos, ballPos);
      if (distToBall < PICKUP_RANGE && ballPos.y < 1.5) {
        const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
        const ballSpeed = ballVel ? ballVel.length() : 0;
        if (ballSpeed < 8) {
          this._pickUpBall();
        }
      }
    }

    const ballImp = this._ball.mesh.physicsImpostor;
    if (ballImp) {
      const currentVel = ballImp.getLinearVelocity()!;
      const dx = currentVel.x - this._lastBallVel.x;
      const dy = currentVel.y - this._lastBallVel.y;
      const dz = currentVel.z - this._lastBallVel.z;
      if (dx*dx + dy*dy + dz*dz > 4.0) {
        this._reactionTimer = GK_REACTION_TIME;
      }
      this._lastBallVel.copyFrom(currentVel);
    }

    if (this._reactionTimer > 0) {
      this._reactionTimer -= dt;
    } else {
      this._prediction = this._predictBall();
    }

    if (this._diving) {
      this._diveTimer -= dt;
      if (this._diveTimer > 0.6) {
        const vx = this._diveDirection * GK_DIVE_SPEED;
        myPos.x += vx * dt;
        this._currentSpeed = GK_DIVE_SPEED;
        this._velocity.set(vx, 0, 0);
      } else if (this._diveTimer > 0) {
        this._currentSpeed = 0;
        this._velocity.set(0, 0, 0);
      } else {
        this._diving = false;
        this._diveTimer = 0;
      }
    } else {
      let targetX = 0;
      let shouldDive = false;

      if (this._prediction && this._prediction.onTarget) {
        targetX = BABYLON.Scalar.Clamp(this._prediction.x, -GOAL_HALF_WIDTH, GOAL_HALF_WIDTH);
        const distToTarget = Math.abs(targetX - myPos.x);
        const timeToArrival = this._prediction.time;
        if (timeToArrival < GK_DIVE_THRESHOLD_TIME && distToTarget > 0.8) {
          shouldDive = true;
        }
      } else {
        const distToGoalZ = Math.abs(ballPos.z - this._goalZ);
        if (distToGoalZ < 25) {
          const depthFactor = Math.max(0, 1 - distToGoalZ / 30);
          targetX = ballPos.x * depthFactor;
        }
      }

      targetX = BABYLON.Scalar.Clamp(targetX, -GOAL_HALF_WIDTH, GOAL_HALF_WIDTH);

      if (shouldDive) {
        this._diving = true;
        this._diveTimer = 1.0;
        this._diveDirection = Math.sign(targetX - myPos.x) || 1;
        this._diveIsHigh = (this._prediction?.y || 0) > 1.2;
        this.animator.triggerDive(this._diveDirection, this._diveIsHigh ? 1 : 0);
      } else {
        const dx = targetX - myPos.x;
        if (Math.abs(dx) > 0.05) {
          const moveDir = Math.sign(dx);
          const moveAmount = Math.min(Math.abs(dx), GK_SPEED * dt);
          myPos.x += moveDir * moveAmount;
          this._currentSpeed = GK_SPEED;
          this._velocity.set(moveDir * GK_SPEED, 0, 0);
        } else {
          this._currentSpeed = 0;
          this._velocity.set(0, 0, 0);
        }
        const angle = Math.atan2(ballPos.x - myPos.x, Math.abs(this._goalZ - ballPos.z));
        this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.2);
      }
    }

    myPos.x = BABYLON.Scalar.Clamp(myPos.x, -GOAL_HALF_WIDTH, GOAL_HALF_WIDTH);
    myPos.z = this._goalZ;
    this.animator.animate(dt, this._currentSpeed, false);
    this._syncCollider();
  }

  private _pickUpBall(): void {
    this._hasBall = true;
    this._isHoldingBall = true;
    this._ballHoldTimer = THROW_HOLD_TIME;
    
    const ballImp = this._ball.mesh.physicsImpostor;
    if (ballImp) {
      ballImp.setLinearVelocity(BABYLON.Vector3.Zero());
      ballImp.setAngularVelocity(BABYLON.Vector3.Zero());
    }
    
    if (!this._ballAttachmentPoint) {
      this._ballAttachmentPoint = new BABYLON.TransformNode("ballAttach", this.mesh.getScene());
      this._ballAttachmentPoint.parent = this.mesh;
      this._ballAttachmentPoint.position = new BABYLON.Vector3(0, 1.2, 0.3);
    }
    
    this._ball.mesh.parent = this._ballAttachmentPoint;
    this._ball.mesh.position = BABYLON.Vector3.Zero();
    this._ball.mesh.rotation = BABYLON.Vector3.Zero();
    
    if (ballImp) {
      ballImp.sleep();
    }
  }

  private _updateBallHolding(dt: number): void {
    this._ballHoldTimer -= dt;
    this._currentSpeed = 0;
    const targetRotation = this._isOpponentGK ? Math.PI : 0;
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, targetRotation, 0.1);
    if (this._ballHoldTimer <= 0) {
      this._throwBall();
    }
    this.animator.animate(dt, 0, false);
    this._syncCollider();
  }

  private _throwBall(): void {
    if (!this._hasBall) return;
    
    this._ball.mesh.parent = null;
    const ballImp = this._ball.mesh.physicsImpostor;
    if (ballImp) {
      ballImp.wakeUp();
    }
    
    const throwTarget = this._findBestThrowTarget();
    const myPos = this.mesh.position;
    
    // Calculate direction to target
    let throwDir = throwTarget.subtract(myPos);
    const distance = throwDir.length();
    
    // If target is too far, scale down to max throw distance
    if (distance > MAX_THROW_DISTANCE) {
      throwDir.normalize().scaleInPlace(MAX_THROW_DISTANCE);
    }
    
    throwDir.normalize();
    throwDir.y = THROW_ARC;
    throwDir.normalize();
    
    this._ball.kick(throwDir, THROW_POWER, this._velocity);
    
    this._hasBall = false;
    this._isHoldingBall = false;
  }

  private _findBestThrowTarget(): BABYLON.Vector3 {
    const myPos = this.mesh.position;
    const teammates = this._getTeammates ? this._getTeammates() : [];
    
    if (teammates.length === 0) {
      // Short throw to middle of field
      const throwZ = this._isOpponentGK ? -8 : 8;
      return new BABYLON.Vector3(myPos.x * 0.5, 0, throwZ);
    }
    
    let bestTeammate: BABYLON.Vector3 | null = null;
    let bestScore = -Infinity;
    
    for (const teammatePos of teammates) {
      const dist = BABYLON.Vector3.Distance(myPos, teammatePos);
      const distFromOurGoal = Math.abs(teammatePos.z - this._goalZ);
      
      // Prefer closer teammates (8-12 units) for accurate short passes
      const distanceScore = 15 - Math.abs(dist - 10);
      const safetyScore = distFromOurGoal * 0.3;
      const score = distanceScore + safetyScore;
      
      if (score > bestScore && dist <= MAX_THROW_DISTANCE) {
        bestScore = score;
        bestTeammate = teammatePos;
      }
    }
    
    // If no good teammate found within range, throw short to middle
    if (!bestTeammate) {
      const throwZ = this._isOpponentGK ? -8 : 8;
      return new BABYLON.Vector3(myPos.x * 0.3, 0, throwZ);
    }
    
    return bestTeammate;
  }

  resetPosition(): void {
    if (this._isHoldingBall) {
      this._ball.mesh.parent = null;
      const ballImp = this._ball.mesh.physicsImpostor;
      if (ballImp) {
        ballImp.wakeUp();
      }
    }
    
    this.mesh.position = new BABYLON.Vector3(0, 0, this._goalZ);
    this.mesh.rotation.y = 0;
    this._diving = false;
    this._diveTimer = 0;
    this._currentSpeed = 0;
    this._hasBall = false;
    this._isHoldingBall = false;
    this._ballHoldTimer = 0;
    this._syncCollider();
  }
}
